import React from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog"
import type { PropsWithChildren } from "react"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu"
import { Tag } from "src/openapi"
import { Button } from "src/components/ui/button"
import { Label } from "src/components/ui/label"
import { InvitationService } from "src/openapi"
import { useAuth } from "src/providers/LoginProvider"
import QRCode from "qrcode"
import AppButton from "src/components/AppButton"
import liff from "@line/liff"

type Props = {
  companyId: number
  tags: Tag[]
}

export default function GenerateInvitationCodeModal({
  companyId,
  tags,
  children,
}: PropsWithChildren<Props>) {
  const { user } = useAuth()
  const [selectedTags, setSelectedTags] = React.useState<Tag[]>([])
  const [invitationCode, setInvitationCode] = React.useState<string>()
  const [invitationCodeDataUrl, setInvitationCodeDataUrl] = React.useState<string>()

  const handleCheckTag = (tag: Tag) => {
    if (selectedTags?.find((selectedTag) => selectedTag.id === tag.id)) {
      setSelectedTags(selectedTags.filter((selectedTag) => selectedTag.id !== tag.id))
    } else {
      setSelectedTags([...(selectedTags ?? []), tag])
    }
  }

  const handleGenerateCode = async () => {
    try {
      const result = await InvitationService.encodeCompanyInvitationCode(
        companyId,
        {
          tagIds: selectedTags.map((tag) => tag.id),
        },
        user?.token
      )
      setInvitationCode(result.invitationCode)
      const targetUrl = await liff.permanentLink.createUrlBy(
        `${location.origin}/line/signup?code=${result.invitationCode}`
      )
      const dataUrl = await QRCode.toDataURL(targetUrl)
      setInvitationCodeDataUrl(dataUrl)
    } catch (e) {
      console.error(e)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedTags([])
      setInvitationCodeDataUrl(undefined)
    }
  }

  const handleDownloadQRCode = () => {
    if (!invitationCodeDataUrl) {
      return
    }
    const tagNames = selectedTags.map((t) => t.tag).join("-")
    const blob = new Blob(
      [Uint8Array.from(atob(invitationCodeDataUrl.split(",")[1]), (c) => c.charCodeAt(0))],
      { type: "image/png" }
    )
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `invitation-code-${companyId}${tagNames ? "-" + tagNames : ""}.png`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <Dialog onOpenChange={handleOpenChange}>
      <DialogTrigger>{children}</DialogTrigger>
      <DialogContent className="bg-white min-w-[600px] min-h-[400px] flex flex-col gap-4 p-8">
        <DialogHeader>
          <DialogTitle>従業員招待コード</DialogTitle>
          <DialogDescription>
            LINEアプリに従業員を招待するためのQRコードを生成します。
            タグを指定することで登録時に自動的にそのタグが付与されます。
          </DialogDescription>
        </DialogHeader>
        <div className="w-full flex flex-1 gap-4">
          <div className="flex flex-col gap-4 items-start justify-between">
            <div className="w-1/2 flex flex-col gap-4">
              <Label>タグ</Label>
              <DropdownMenu>
                <DropdownMenuTrigger>
                  {selectedTags.length > 0 ? (
                    <Button variant="outline" className="w-[200px]">
                      {selectedTags.map((tag) => tag.tag).join(", ")}
                    </Button>
                  ) : (
                    <Button variant="outline" className="w-[200px]">
                      タグを選択
                    </Button>
                  )}
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {tags.map((tag) => (
                    <DropdownMenuCheckboxItem
                      key={tag.id}
                      checked={selectedTags.findIndex((t) => t.id === tag.id) >= 0}
                      onClick={() => handleCheckTag(tag)}
                    >
                      {tag.tag}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <Button variant="default" onClick={handleGenerateCode}>
              QRコードを生成
            </Button>
          </div>
          <div className="flex flex-col gap-4">
            {invitationCodeDataUrl && (
              <>
                <img src={invitationCodeDataUrl} alt="QRコード" />
                <AppButton variant="secondary" onClick={() => handleDownloadQRCode()}>
                  QRコードをダウンロード
                </AppButton>
              </>
            )}
            {invitationCode && window.location.hostname === "localhost" && (
              <a href={`http://localhost:3000/line/signup?code=${invitationCode}`}>招待リンク</a>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
